/* «1. Видео с движениями» (§20.1 п. 2, BotCore.upload вида video): пустая рамка — пример «до → после» на месте значка, слова пресета,
   «Загрузить» и «Снять» (камера пресета), ограничения строкой; с роликом — кадр в пропорциях ролика, длительность, «Заменить · Кадр · Убрать»;
   нажатие на кадр смотрит фрагмент в той же рамке; «Кадр» — фрагмент под рамкой (у ролика длиннее предела — всегда).
   Бейдж цены за единицу — в правом верхнем углу (только из серверной цены). Файл уходит загрузкой формы (state.upload) после согласия. */
import { useRef, useState, type ChangeEvent, type CSSProperties, type ReactNode } from 'react';
import { uiFormat, uiText } from '../../i18n';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import type { StudioForm } from '../studio/studio-api';
import { ToolIcon } from '../studio/circle/ToolIcon';
import { mmss } from '../studio/circle/order-tail-model';
import { StudioSheet } from '../studio/StudioSheet';
import { uploadLimits } from '../studio/studio-upload-model';
import { useVideoSourcePreview } from '../video/use-video-source-preview';
import { fill, motionFragment, type MotionWords } from './motion-model';
import { MotionConsent } from './MotionConsent';

export interface MotionClipMeta {
  name: string;
  width: number;
  height: number;
  durationMs: number;
  /** Есть ли звук: только если браузер это точно знает (audioTracks, mozHasAudio); иначе null */
  hasAudio: boolean | null;
}
const TRIM_KEYS = ['start_ms', 'end_ms'];
function audioOf(video: HTMLVideoElement): boolean | null {
  const v = video as HTMLVideoElement & { audioTracks?: { length: number }; mozHasAudio?: boolean };
  if (v.audioTracks && typeof v.audioTracks.length === 'number') return v.audioTracks.length > 0;
  if (typeof v.mozHasAudio === 'boolean') return v.mozHasAudio;
  return null;
}
export function MotionClip({
  field,
  state,
  words,
  limits,
  badge,
  example,
  meta,
  onMeta,
}: {
  field: StudioForm['fields'][number];
  state: CircleFormState;
  words: MotionWords;
  limits: { minMs: number; maxMs: number };
  badge?: ReactNode;
  example: ReactNode;
  meta: MotionClipMeta | null;
  onMeta(value: MotionClipMeta | null): void;
}) {
  const [error, setError] = useState('');
  const [waiting, setWaiting] = useState<File | null>(null);
  const [cut, setCut] = useState(false);
  const [playing, setPlaying] = useState(false);
  const video = useRef<HTMLVideoElement>(null);
  const replace = useRef<HTMLInputElement>(null);
  const name = useRef('');
  const maximum = field.upload?.max_bytes ?? 100 * 1024 * 1024;
  const preview = useVideoSourcePreview(state.inputs[field.key], state.ownerId, 'video', maximum);
  const has = !!preview.url || !!state.inputs[field.key] || !!state.uploadIds[field.key];
  const trimAvailable = TRIM_KEYS.every(key => state.form?.fields.some(f => f.key === key));
  const min = limits.minMs / 1000,
    max = limits.maxMs / 1000;
  const clear = () => {
    state.clearUpload(field.key);
    preview.select(null);
    onMeta(null);
    setCut(false);
    setPlaying(false);
    state.setInputs(current => {
      const next = { ...current };
      for (const key of TRIM_KEYS) delete next[key];
      return next;
    });
  };
  const select = (file: File | undefined) => {
    if (!file || state.locked) return;
    setError('');
    if (!field.upload!.accept.includes(file.type)) {
      setError('Нужно видео: MP4 или MOV');
      return;
    }
    if (file.size > maximum) {
      setError(uiFormat('Файл слишком большой. Максимум {0} МБ', Math.round(maximum / 1024 / 1024)));
      return;
    }
    if (!state.rights || state.inputs.consent_accepted !== 'true') {
      setWaiting(file);
      return;
    }
    if (state.inputs[field.key] || state.uploadIds[field.key]) clear();
    setCut(false);
    name.current = file.name;
    preview.select(file);
    void state.upload(field.key, file);
  };
  const fragment = motionFragment(state.inputs);
  const duration = meta?.durationMs ?? 0;
  const long = duration > limits.maxMs;
  const showTrim = trimAvailable && has && duration > 0 && (long || cut);
  const toggle = () => {
    const v = video.current;
    if (!v) return;
    if (!v.paused) {
      v.pause();
      setPlaying(false);
      return;
    }
    const start = (fragment?.startMs ?? 0) / 1000;
    if (v.currentTime < start || (fragment && v.currentTime >= fragment.endMs / 1000)) v.currentTime = start;
    try {
      const run = v.play();
      if (run && typeof run.catch === 'function')
        run.catch(() => {
          v.muted = true;
          void v.play()?.catch?.(() => setPlaying(false));
        });
      setPlaying(true);
    } catch {
      setPlaying(false);
    }
  };
  const ratio = meta && meta.width > 0 && meta.height > 0 ? Math.round((meta.width / meta.height) * 1e4) / 1e4 : 0.5625;
  const inputProps = {
    type: 'file',
    className: 'studio-native-file-input',
    disabled: state.locked,
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      select(e.target.files?.[0]);
      e.target.value = '';
    },
  } as const;
  return (
    <div className="pf-up dn-up motion-up">
      <div className="glbl dn-l1">{uiText(words.clipLabel)}</div>
      <div className={`bcu motion-bcu${has ? ' has' : ''}`} style={{ '--dn-r': String(ratio) } as CSSProperties}>
        {!has ? (
          <div className="bcu-st" data-k="empty">
            <div className="bcu-e">
              <span className="bcu-ic motion-ex-ic">{example}</span>
              <b className="bcu-t">{uiText(words.upload)}</b>
              <span className="bcu-s">{fill(uiText(words.sub), min, max)}</span>
              <div className="bcu-acts">
                <label className="bcu-pl pri">
                  <ToolIcon kind="upload" />
                  {uiText('Загрузить')}
                  <input {...inputProps} aria-label={uiText('Загрузить видео')} accept={field.upload!.accept.join(',')} />
                </label>
                <label className="bcu-pl">
                  <ToolIcon kind="camera" />
                  {uiText('Снять')}
                  <input {...inputProps} aria-label={uiText('Снять видео')} accept="video/*" capture={words.camera} />
                </label>
              </div>
              <small className={`bcu-n${error ? ' bad' : ''}`}>
                {error ? uiText(error) : `${uploadLimits(field)}, ${uiFormat('из длинного выберете {0} секунд', max)}`}
              </small>
            </div>
          </div>
        ) : (
          <div className="bcu-st" data-k="file">
            <div className="bcu-f dk">
              <div className="bcu-pa">
                {preview.url ? (
                  <div
                    className={`bcu-pv motion-pv${playing ? ' dn-on' : ''}`}
                    role="button"
                    tabIndex={0}
                    aria-label={uiText(playing ? 'Пауза' : 'Смотреть видео с движениями')}
                    onClick={toggle}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle();
                      }
                    }}
                  >
                    <video
                      ref={video}
                      playsInline
                      preload="metadata"
                      src={preview.url}
                      onLoadedMetadata={e => {
                        const v = e.currentTarget;
                        const ms = Math.floor(v.duration * 1000);
                        if (!Number.isFinite(ms) || ms <= 0) return;
                        onMeta({ name: name.current, width: v.videoWidth, height: v.videoHeight, durationMs: ms, hasAudio: audioOf(v) });
                        if (trimAvailable)
                          state.setInputs(current =>
                            current.end_ms !== undefined
                              ? current
                              : { ...current, start_ms: current.start_ms ?? '0', end_ms: String(Math.min(ms, limits.maxMs)) },
                          );
                      }}
                      onTimeUpdate={e => {
                        const end = (fragment?.endMs ?? Infinity) / 1000;
                        if (!e.currentTarget.paused && e.currentTarget.currentTime >= end) {
                          e.currentTarget.pause();
                          setPlaying(false);
                        }
                      }}
                      onEnded={() => setPlaying(false)}
                    />
                    <span className="bcu-play" aria-hidden="true">
                      <ToolIcon kind={playing ? 'pause' : 'play'} />
                    </span>
                    {duration > 0 && (
                      <span className="bcu-dur">
                        <ToolIcon kind="video" />
                        <b>{mmss(duration / 1000)}</b>
                      </span>
                    )}
                  </div>
                ) : preview.loading || state.busy ? (
                  <p role="status">{uiText('Загружаем…')}</p>
                ) : preview.error ? (
                  <p role="alert">
                    {uiText('Не удалось прочитать файл.')}{' '}
                    <button type="button" onClick={preview.retry}>
                      {uiText('Повторить')}
                    </button>
                  </p>
                ) : null}
              </div>
              <div className="bcu-fa">
                <button type="button" className="bcu-pl gl" disabled={state.locked} onClick={() => replace.current?.click()}>
                  <ToolIcon kind="refresh" />
                  {uiText('Заменить')}
                </button>
                {trimAvailable && (
                  <button type="button" className="bcu-pl gl" disabled={state.locked || !duration} onClick={() => setCut(true)}>
                    <ToolIcon kind="scissors" />
                    {uiText('Кадр')}
                  </button>
                )}
                <button type="button" className="bcu-pl gl" disabled={state.locked} onClick={clear}>
                  <ToolIcon kind="trash" />
                  {uiText('Убрать')}
                </button>
                <input {...inputProps} ref={replace} hidden aria-label={uiText('Заменить видео')} accept={field.upload!.accept.join(',')} />
              </div>
            </div>
          </div>
        )}
        {badge}
      </div>
      {has && error && <p className="bcu-n bad" role="alert">{uiText(error)}</p>}
      {state.uploadIds[field.key] && !state.inputs[field.key] && !state.busy && (
        <button type="button" className="ar-lnk" disabled={state.locked || !state.rights} onClick={() => void state.upload(field.key, null)}>
          {uiText('Продолжить загрузку')}
        </button>
      )}
      <div className="pf-trw">
        {showTrim && <MotionTrim state={state} durationMs={duration} limits={limits} />}
      </div>
      {waiting && (
        <StudioSheet title="Права и согласие" onClose={() => setWaiting(null)}>
          <h2>{uiText('Права и согласие')}</h2>
          <MotionConsent state={state} />
          <button
            type="button"
            className="btn"
            disabled={state.locked || !state.rights || state.inputs.consent_accepted !== 'true'}
            onClick={() => {
              const file = waiting;
              setWaiting(null);
              if (state.inputs[field.key] || state.uploadIds[field.key]) clear();
              name.current = file.name;
              preview.select(file);
              void state.upload(field.key, file);
            }}
          >
            {uiText('Продолжить загрузку')}
          </button>
        </StudioSheet>
      )}
    </div>
  );
}
/** Фрагмент под рамкой (.ap-trim): начало и конец окна; предел — по выбранному фону. Окно не обрезается молча при смене фона. */
export function MotionTrim({ state, durationMs, limits }: { state: CircleFormState; durationMs: number; limits: { minMs: number; maxMs: number } }) {
  const fragment = motionFragment(state.inputs) ?? { startMs: 0, endMs: Math.min(durationMs, limits.maxMs), lenMs: Math.min(durationMs, limits.maxMs) };
  const set = (key: 'start_ms' | 'end_ms', seconds: number) =>
    state.setInputs(current => {
      const ms = Math.round(Math.max(0, Math.min(durationMs, seconds * 1000)));
      const next = { ...current, [key]: String(ms) };
      const start = Number(next.start_ms ?? 0),
        end = Number(next.end_ms ?? durationMs);
      if (key === 'start_ms' && end <= start) next.end_ms = String(Math.min(durationMs, start + limits.minMs));
      if (key === 'end_ms' && end <= start) next.start_ms = String(Math.max(0, end - limits.minMs));
      return next;
    });
  return (
    <div className="ap-trim motion-trim" role="group" aria-label={uiText('Фрагмент')}>
      <div className="th">
        <span className="lab">
          {uiText('Фрагмент')} · <span className="mono">{mmss(fragment.lenMs / 1000)}</span> {uiText('из')}{' '}
          <span className="mono">{mmss(durationMs / 1000)}</span>
        </span>
        <span className="n">{uiFormat('максимум {0} секунд', limits.maxMs / 1000)}</span>
      </div>
      {(['start_ms', 'end_ms'] as const).map(key => (
        <label key={key} className="motion-trim-row">
          <span>{uiText(key === 'start_ms' ? 'Начало' : 'Конец')}</span>
          <input
            type="range"
            min={0}
            max={durationMs / 1000}
            step={0.1}
            disabled={state.locked}
            value={(key === 'start_ms' ? fragment.startMs : fragment.endMs) / 1000}
            aria-label={uiText(key === 'start_ms' ? 'Начало фрагмента, секунды' : 'Конец фрагмента, секунды')}
            onChange={e => set(key, Number(e.target.value))}
          />
          <span className="mono">{mmss((key === 'start_ms' ? fragment.startMs : fragment.endMs) / 1000)}</span>
        </label>
      ))}
    </div>
  );
}
